import React from "react";

// =============================================================================
// Tiny presentational primitives shared across wizard steps.
// Kept here so step files focus on logic, not on layout boilerplate.
// =============================================================================

export const Field = ({ label, hint, required, children, testid }) => (
  <label className="block" data-testid={testid ? `field-${testid}` : undefined}>
    <span className="text-xs uppercase tracking-[0.18em] text-stone-400 font-medium">
      {label}{required && <span className="text-rose-400 ml-1">*</span>}
    </span>
    <div className="mt-1.5">{children}</div>
    {hint && <p className="mt-1 text-[11px] text-stone-500">{hint}</p>}
  </label>
);

export const Input = React.forwardRef(({ className = "", ...props }, ref) => (
  <input
    ref={ref}
    className={`w-full h-11 px-3 rounded-lg bg-stone-900/60 border border-stone-700/60 focus:border-amber-500/60 focus:ring-1 focus:ring-amber-500/40 outline-none text-stone-100 placeholder-stone-500 text-sm transition ${className}`}
    {...props}
  />
));
Input.displayName = "WizardInput";

export const Textarea = React.forwardRef(({ className = "", ...props }, ref) => (
  <textarea
    ref={ref}
    className={`w-full px-3 py-2.5 rounded-lg bg-stone-900/60 border border-stone-700/60 focus:border-amber-500/60 focus:ring-1 focus:ring-amber-500/40 outline-none text-stone-100 placeholder-stone-500 text-sm transition min-h-[110px] resize-y ${className}`}
    {...props}
  />
));
Textarea.displayName = "WizardTextarea";

export const Select = React.forwardRef(({ className = "", children, ...props }, ref) => (
  <select
    ref={ref}
    className={`w-full h-11 px-3 rounded-lg bg-stone-900/60 border border-stone-700/60 focus:border-amber-500/60 focus:ring-1 focus:ring-amber-500/40 outline-none text-stone-100 text-sm transition ${className}`}
    {...props}
  >{children}</select>
));
Select.displayName = "WizardSelect";

export const Chip = ({ active, onClick, children, testid, disabled }) => (
  <button
    type="button"
    disabled={disabled}
    onClick={onClick}
    data-testid={testid}
    className={`px-3 py-1.5 rounded-full text-xs font-medium border transition select-none whitespace-nowrap
      ${active
        ? "bg-amber-500/15 border-amber-500/60 text-amber-200 shadow-[0_0_0_1px_rgba(245,158,11,0.25)]"
        : "bg-stone-900/40 border-stone-700/60 text-stone-300 hover:border-stone-500 hover:text-stone-100"}
      ${disabled ? "opacity-40 cursor-not-allowed" : ""}`}
  >{children}</button>
);

export const ChipGroup = ({ options, value = [], onChange, testidPrefix }) => {
  const arr = Array.isArray(value) ? value : [];
  const toggle = (v) => onChange(
    arr.includes(v) ? arr.filter(x => x !== v) : [...arr, v]
  );
  return (
    <div className="flex flex-wrap gap-2">
      {options.map((o) => {
        const v = typeof o === "string" ? o : o.v ?? o.k;
        const label = typeof o === "string" ? o : o.label;
        return (
          <Chip
            key={v}
            active={arr.includes(v)}
            onClick={() => toggle(v)}
            testid={testidPrefix ? `${testidPrefix}-${v}` : undefined}
          >{label}</Chip>
        );
      })}
    </div>
  );
};

export const Toggle = ({ checked, onChange, label, testid }) => (
  <button
    type="button"
    onClick={() => onChange(!checked)}
    data-testid={testid}
    className="flex items-center gap-3 w-full text-left"
  >
    <span className={`relative inline-flex w-10 h-6 rounded-full transition ${checked ? "bg-amber-500" : "bg-stone-700"}`}>
      <span className={`absolute top-0.5 ${checked ? "left-[18px]" : "left-0.5"} w-5 h-5 bg-white rounded-full transition-all shadow`} />
    </span>
    <span className="text-sm text-stone-200">{label}</span>
  </button>
);

export const SectionTitle = ({ icon: Icon, title, subtitle }) => (
  <div className="mb-5">
    <div className="flex items-center gap-2">
      {Icon && <Icon className="w-4 h-4 text-amber-400" />}
      <h3 className="text-base sm:text-lg font-serif text-stone-100">{title}</h3>
    </div>
    {subtitle && <p className="mt-1 text-xs text-stone-500">{subtitle}</p>}
  </div>
);
